import React, { useState } from 'react';
import {
    Box,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel,
    TextField
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ProceedButton from '../../Components/Buttons/ProceedButton';
import { useAuth } from '../../Context/AuthContext';
import { FOODIMETRIC_HOST_URL } from '../../Utils/host';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

// Custom styled Radio
const CustomRadio = styled(Radio)({
    color: '#3a8589',
    '&.Mui-checked': {
        color: '#3a8589',
    },
});

const IBW = () => {
    const { user, logout } = useAuth();
    const [height, setHeight] = useState('170');
    const [gender, setGender] = useState('male');
    const [ibw, setIbw] = useState(null);

    const handleHeightChange = (event) => setHeight(event.target.value);
    const handleGenderChange = (e) => setGender(e.target.value);

    const calculateIBW = () => {
        const numericHeight = parseFloat(height);
        let ibwValue;
        if (gender === 'male') {
            ibwValue = 50 + 0.91 * (numericHeight - 152.4);
        } else {
            ibwValue = 45.5 + 0.91 * (numericHeight - 152.4);
        }
        setIbw(ibwValue);
        return ibwValue;
    };

    const handleProceed = async () => {
        const ibw_value = calculateIBW();
        if (!ibw_value) return;

        const calculationPayload = {
            user_id: user._id,
            calculator_name: "IBW",
            parameters: {
                height: `${height} cm`,
                gender: gender,
            },
            result: `${ibw_value} kg`,
            calculation_details: "IBW calculated using Devine formula with height in cm and gender",
        };

        try {
            const response = await fetch(`${FOODIMETRIC_HOST_URL}/calculations`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user.token}`,
                },
                body: JSON.stringify(calculationPayload),
            });

            if (response.status === 401) {
                logout()
                window.location.href = "/login";
                return; // Prevent further code execution
            }

            if (!response.ok) {
                const error = await response.json();
                console.error("Failed to save calculation:", error);
            }
        } catch (err) {
            console.error("Error saving calculation:", err);
        }
    };

    return (
        <main className="py-8">
            <Helmet>
                <title>IBW Calculator for Men & Women – Ideal Body Weight Calculation & Formula</title>
                <meta name="description" content="Find your Ideal Body Weight (IBW) with our free calculator for men and women. See IBW charts, formulas, and guidelines to achieve a healthy weight." />
                <meta name="keywords" content="IBW calculator, ideal body weight, healthy weight range, IBW formula, weight chart, men and women weight guide, body mass index, weight management, Foodimetric IBW, IBW, IBW calculator" />
                <script type="application/ld+json">
                    {`{
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "IBW Calculator",
    "description": "Use our IBW calculator to determine your ideal body weight.",
    "url": "https://www.foodimetric.com/anthro/IBW"
  }`}
                </script>

            </Helmet>

            <div className="bg-white p-6 md:p-10 mx-auto">
                <h1 className="text-3xl font-bold mb-4 text-center">IBW Calculator for Men & Women</h1>
                <Typography variant="body1" sx={{ mb: 4, fontFamily: 'Outfit, serif' }}>
                    This Ideal Body Weight (IBW) calculator uses the <strong>Devine Formula (1974)</strong> to estimate your optimal body weight based on height and gender. Widely used in medical and fitness fields, this method helps guide health goals and dosage calculations.
                </Typography>

                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ fontFamily: 'Itim, cursive' }}>Enter Height (in cm)</Typography>
                    <TextField
                        type="number"
                        value={height}
                        onChange={handleHeightChange}
                        inputProps={{ min: 105, max: 400, step: 1 }}
                        sx={{ width: '100%', mt: 2 }}
                    />
                </Box>

                <Box sx={{ mb: 4 }}>
                    <FormLabel component="legend" sx={{ fontFamily: 'Itim, cursive' }}>Select Gender</FormLabel>
                    <RadioGroup
                        row
                        value={gender}
                        onChange={handleGenderChange}
                        aria-label="gender"
                        name="gender"
                    >
                        <FormControlLabel value="male" control={<CustomRadio />} label="Male" />
                        <FormControlLabel value="female" control={<CustomRadio />} label="Female" />
                    </RadioGroup>
                </Box>

                <ProceedButton color="#ffba08" type="button" onClick={handleProceed} />

                {ibw !== null && (
                    <Box sx={{ mt: 4, textAlign: 'center' }}>
                        <Typography variant="h6">Your Ideal Body Weight: {ibw.toFixed(2)} kg</Typography>
                        <Typography sx={{ mt: 1, fontStyle: 'italic' }}>
                            Calculated using the B.J. Devine Formula (1974)
                        </Typography>
                    </Box>
                )}

                <Box sx={{ mt: 6 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Itim, cursive' }}>About the Devine Formula</Typography>
                    <Typography sx={{ fontSize: '1rem', mb: 2, fontFamily: 'Itim, cursive' }}>
                        The <strong>Devine Formula</strong> was developed by Dr. B.J. Devine in 1974 to calculate appropriate drug dosages based on body weight. It became a standard for estimating healthy weight ranges.
                    </Typography>
                </Box>

                <Box sx={{ mt: 6 }}>
                    <Typography variant="h5" sx={{ fontFamily: 'Outfit, serif' }}>Frequently Asked Questions (FAQs)</Typography>
                    <Box sx={{ mt: 2 }}>
                        <Typography sx={{ mt: 2, fontFamily: 'Outfit, serif' }}><strong>How do I calculate IBW using the Devine Formula?</strong></Typography>
                        <Typography sx={{ mb: 2, fontFamily: 'Itim, cursive' }}>
                            For males: 50 + 0.91 × (height in cm – 152.4). For females: 45.5 + 0.91 × (height in cm – 152.4).
                        </Typography>

                        <Typography sx={{ mt: 2, fontFamily: 'Outfit, serif' }}><strong>What is the Devine Formula used for?</strong></Typography>
                        <Typography sx={{ mb: 2, fontFamily: 'Itim, cursive' }}>
                            Originally for calculating medication doses, it’s now widely used to estimate ideal body weight in clinical and fitness settings.
                        </Typography>

                        <Typography sx={{ mt: 2, fontFamily: 'Outfit, serif' }}><strong>Can the Devine formula be used for adolescents?</strong></Typography>
                        <Typography sx={{ mb: 2, fontFamily: 'Itim, cursive' }}>
                            No, it’s meant for adults. For adolescents, pediatric growth charts are more appropriate.
                        </Typography>

                        <Typography sx={{ mt: 2, fontFamily: 'Outfit, serif' }} ><strong>Is Ideal Body Weight the same as BMI?</strong></Typography>
                        <Typography sx={{ mt: 2, fontFamily: 'Itim, cursive' }}>
                            No. BMI measures weight relative to height, while IBW estimates optimal weight based only on height and gender.
                        </Typography>
                    </Box>
                </Box>


                <Box sx={{ mt: 6, fontSize: '0.9rem', color: 'gray', fontFamily: 'Itim, cursive' }}>
                    <Typography sx={{ fontFamily: 'Itim, cursive' }}>
                        Source:
                        Devine, BJ. Gentamicin therapy. Drug Intell Clin Pharm 1974;8:650–5.
                    </Typography>
                </Box>

                <Box sx={{ mt: 4, fontFamily: 'Itim, cursive' }}>
                    <Typography>Explore more tools:</Typography>
                    <ul>
                        <li><Link to="/anthro/WHR" style={{ color: '#3a8589' }}>WHR Calculator</Link></li>
                        <li><Link to="/anthro/BMR" style={{ color: '#3a8589' }}>BMR Calculator</Link></li>
                    </ul>
                </Box>
            </div>
        </main>
    );
};

export default IBW;
